import { rtkApi } from 'src/Services/rtkApi'
import { APP } from 'src/Configs/App'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { MILLISECONDS_IN_SECONDS } from 'src/Constants/numeric'

import { getClientId, getGaSession } from './helpers'

export const HEADER_ERROR = 'x-scga-header-error'

export const getAnalyticsHeaders = (): Record<string, string> => ({
  'x-scga-session': getGaSession(),
  'x-scga-client-id': getClientId(),
  'x-scga-timestamp': String(Date.now() * MILLISECONDS_IN_SECONDS),
  'x-scga-page-title': window.location.href,
})

export interface GA4Response {
  success: boolean
  message?: string
}

export interface AddedResponse {
  success: boolean
  message?: string
}

interface PaymentEvent {
  campaignId: number
  campaignPlatform: string
}

interface Checkout extends PaymentEvent {
  paymentMethod: string
  price: number
}

interface FirstCampaignCreation {
  campaignPlatform: CampaignPlatformTypeEnum
  referrerUrl?: string
  utmCampaign?: string
  utmContent?: string
  utmMedium?: string
  utmSource?: string
  utmTerm?: string
}

interface NewCampaignCreation {
  campaignPlatform: CampaignPlatformTypeEnum
  campaignType: string
}

interface AddingGenreOrLanguage {
  campaignId: number | string
  campaignPlatform: CampaignPlatformTypeEnum
}

interface TrackAdding {
  campaignPlatform: CampaignPlatformTypeEnum
  trackId: string
  userId?: number
  campaignId?: number
}

interface ScheduleStart {
  campaignId: number
  campaignPlatform: CampaignPlatformTypeEnum
}

interface GetGtmInfoInterface {
  genresList: string[]
  languagesList: string[]
  campaignNumber: string
  campaignType: string
  resultPrice: number
  value: number
  transactionId: string
}

const analyticsApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getPaymentGtm: build.query<
      GetGtmInfoInterface,
      { platform: string; campaignId: number }
    >({
      query: ({ platform, campaignId }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/analytic/payment-gtm/${platform}/${campaignId}`,
      }),
    }),

    paymentEventCompleted: build.mutation<GA4Response, PaymentEvent>({
      query: (data) => ({
        url: `${APP.TIKTOK_SERVER}/artist/analytic/payment-events/completed`,
        method: 'POST',
        headers: getAnalyticsHeaders(),
        body: data,
      }),
    }),

    paymentEventCancelled: build.mutation<GA4Response, PaymentEvent>({
      query: (data) => ({
        url: `${APP.TIKTOK_SERVER}/artist/analytic/payment-events/cancelled`,
        method: 'POST',
        headers: getAnalyticsHeaders(),
        body: data,
      }),
    }),

    checkoutStart: build.mutation<GA4Response, Checkout>({
      query: (data) => ({
        url: `${APP.TIKTOK_SERVER}/artist/analytic/payment-events/checkout-start`,
        method: 'POST',
        headers: getAnalyticsHeaders(),
        body: data,
      }),
    }),

    firstCampaignCreationStart: build.mutation<
      GA4Response,
      FirstCampaignCreation
    >({
      query: (params) => {
        const {
          campaignPlatform,
          referrerUrl,
          utmCampaign,
          utmContent,
          utmMedium,
          utmSource,
          utmTerm,
        } = params

        const body = {
          campaignPlatform,
          isFeed: campaignPlatform === CampaignPlatformTypeEnum.SPOTIFY,
          ...(referrerUrl ? { referrerUrl: document.referrer } : {}),
          ...(utmCampaign ? { utmCampaign } : {}),
          ...(utmContent ? { utmContent } : {}),
          ...(utmMedium ? { utmMedium } : {}),
          ...(utmSource ? { utmSource } : {}),
          ...(utmTerm ? { utmTerm } : {}),
        }

        return {
          url: `${APP.TIKTOK_SERVER}/public/analytic/campaign-events/first-campaign-creation-start`,
          method: 'POST',
          headers: getAnalyticsHeaders(),
          body,
        }
      },
    }),

    newCampaignCreationStart: build.mutation<GA4Response, NewCampaignCreation>({
      query: (data) => ({
        url: `${APP.TIKTOK_SERVER}/artist/analytic/campaign-events/new-campaign-creation-start`,
        method: 'POST',
        headers: getAnalyticsHeaders(),
        body: data,
      }),
    }),

    genreAdded: build.mutation<AddedResponse, AddingGenreOrLanguage>({
      query: (data) => ({
        url: `${APP.TIKTOK_SERVER}/artist/analytic/campaign-events/genre-added`,
        method: 'POST',
        headers: getAnalyticsHeaders(),
        body: data,
      }),
    }),

    languageAdded: build.mutation<AddedResponse, AddingGenreOrLanguage>({
      query: (data) => ({
        url: `${APP.TIKTOK_SERVER}/artist/analytic/campaign-events/language-added`,
        method: 'POST',
        headers: getAnalyticsHeaders(),
        body: data,
      }),
    }),

    trackAdded: build.mutation<GA4Response, TrackAdding>({
      query: (data) => ({
        url: `${APP.TIKTOK_SERVER}/artist/analytic/campaign-events/track-added`,
        method: 'POST',
        headers: getAnalyticsHeaders(),
        body: data,
      }),
    }),

    scheduleStart: build.mutation<GA4Response, ScheduleStart>({
      query: (data) => ({
        url: `${APP.TIKTOK_SERVER}/artist/analytic/campaign-events/schedule-start`,
        method: 'POST',
        headers: getAnalyticsHeaders(),
        body: data,
      }),
    }),

    publicTrackAdded: build.mutation<GA4Response, TrackAdding>({
      query: (data) => ({
        url: `${APP.TIKTOK_SERVER}/public/analytic/campaign-events/track-added`,
        method: 'POST',
        headers: getAnalyticsHeaders(),
        body: data,
      }),
    }),
  }),
})

export const {
  useGetPaymentGtmQuery,
  usePaymentEventCompletedMutation,
  usePaymentEventCancelledMutation,
  useCheckoutStartMutation,
  useFirstCampaignCreationStartMutation,
  useNewCampaignCreationStartMutation,
  useGenreAddedMutation,
  useLanguageAddedMutation,
  useTrackAddedMutation,
  useScheduleStartMutation,
  usePublicTrackAddedMutation,
} = analyticsApi
